import { type Table } from '@tanstack/react-table';
import { useCallback, useLayoutEffect, useRef, type RefObject } from 'react';
import { type TableAutoPageSizeConfig } from '@avanta/shared/types/table';

interface UseAutoPageSizeProps<T> {
  table: Table<T>;
  tableRef: RefObject<HTMLTableElement | null>;
  config?: boolean | TableAutoPageSizeConfig;
  pageSize?: number;
  isLoading: boolean;
  /** When false, measurements pause and the manual page size is kept. */
  isActive?: boolean;
}

const RESIZE_DELAY = 120;
// Layout keeps changing while the page loads (skeletons, headings, pagination appearing).
// After the first fit, a new size is only applied once it holds, to avoid redundant requests.
const SIZE_SETTLE_DELAY = 250;
const pixels = (value: string): number => Number.parseFloat(value) || 0;

export const useAutoPageSize = <T>({
  table,
  tableRef,
  config,
  pageSize,
  isLoading,
  isActive = true,
}: UseAutoPageSizeProps<T>): {
  frameRef: RefObject<HTMLDivElement | null>;
  footerRef: RefObject<HTMLDivElement | null>;
} => {
  const frameRef = useRef<HTMLDivElement>(null);
  const footerRef = useRef<HTMLDivElement>(null);
  const initialPageSize = useRef(table.getState().pagination.pageSize);
  const rowMeasurement = useRef({ width: 0, height: 0 });
  const hasAutoSize = useRef(false);
  const settleTimer = useRef<number | undefined>(undefined);
  const enabled = Boolean(config) && isActive;
  const options = typeof config === 'object' ? config : undefined;
  const minRows = Math.max(1, Math.floor(options?.minRows ?? 1));
  const maxRows = Math.max(minRows, Math.floor(options?.maxRows ?? 50));
  const bottomOffset = Math.max(0, options?.bottomOffset ?? 0);
  const basePageSize = pageSize ?? initialPageSize.current;

  const cancelPendingSize = useCallback((): void => {
    window.clearTimeout(settleTimer.current);
    settleTimer.current = undefined;
  }, []);

  const measure = useCallback((): void => {
    const frame = frameRef.current;
    const element = tableRef.current;
    const footer = footerRef.current;
    const updateSize = (size: number): void => {
      if (table.getState().pagination.pageSize !== size) table.setPageSize(size);
    };

    if (!enabled || !frame || !element || !footer) {
      cancelPendingSize();
      // Manual selection: pause auto without overwriting the chosen page size.
      if (!isActive) {
        hasAutoSize.current = false;
        return;
      }
      if (hasAutoSize.current) {
        hasAutoSize.current = false;
        updateSize(basePageSize);
      }
      return;
    }

    const frameRect = frame.getBoundingClientRect();
    if (frameRect.width <= 0) {
      cancelPendingSize();
      // The desktop frame is hidden in the card layout; restore its original page size.
      if (hasAutoSize.current) {
        hasAutoSize.current = false;
        updateSize(basePageSize);
      }
      return;
    }

    const elementRect = element.getBoundingClientRect();
    if (elementRect.width <= 0) return;
    if (rowMeasurement.current.width !== elementRect.width) {
      rowMeasurement.current = { width: elementRect.width, height: 0 };
    }

    const bodyRows = Array.from(element.tBodies[0]?.rows ?? []).filter(
      row => !row.querySelector('td[colspan]')
    );
    const measuredHeight = Math.max(0, ...bodyRows.map(row => row.getBoundingClientRect().height));
    if (!isLoading || rowMeasurement.current.height === 0) {
      // Keep the tallest encountered row at this width to avoid oscillating between pages.
      rowMeasurement.current.height = Math.max(rowMeasurement.current.height, measuredHeight);
    }
    const rowHeight = rowMeasurement.current.height;
    if (rowHeight <= 0) return;

    const style = window.getComputedStyle(element);
    const spacing =
      style.borderCollapse === 'collapse'
        ? 0
        : pixels(style.borderSpacing.split(' ').at(-1) ?? '0');
    const headerHeight = element.tHead?.getBoundingClientRect().height ?? 0;
    const frameStyle = window.getComputedStyle(frame);
    const frameBottom = pixels(frameStyle.paddingBottom) + pixels(frameStyle.borderBottomWidth);
    const scrollbarHeight = Math.max(
      0,
      frame.offsetHeight -
        frame.clientHeight -
        pixels(frameStyle.borderTopWidth) -
        pixels(frameStyle.borderBottomWidth)
    );
    let ancestorPadding = 0;
    for (let parent = frame.parentElement; parent; parent = parent.parentElement) {
      ancestorPadding += pixels(window.getComputedStyle(parent).paddingBottom);
    }

    // Use document coordinates: scrolling must not change the available capacity.
    const tableTop = elementRect.top + window.scrollY;
    const availableHeight =
      window.innerHeight -
      tableTop -
      headerHeight -
      spacing * 2 -
      footer.getBoundingClientRect().height -
      frameBottom -
      scrollbarHeight -
      ancestorPadding -
      bottomOffset;
    const nextPageSize = Math.min(
      maxRows,
      Math.max(minRows, Math.floor(availableHeight / (rowHeight + spacing)))
    );
    cancelPendingSize();
    if (nextPageSize === table.getState().pagination.pageSize) {
      hasAutoSize.current = true;
      return;
    }
    if (!hasAutoSize.current) {
      hasAutoSize.current = true;
      updateSize(nextPageSize);
      return;
    }
    settleTimer.current = window.setTimeout(() => {
      settleTimer.current = undefined;
      updateSize(nextPageSize);
    }, SIZE_SETTLE_DELAY);
  }, [
    enabled,
    isActive,
    table,
    tableRef,
    isLoading,
    minRows,
    maxRows,
    bottomOffset,
    basePageSize,
    cancelPendingSize,
  ]);

  useLayoutEffect(() => cancelPendingSize, [cancelPendingSize]);

  // Covers React updates to filters, actions, rows and loading state before painting.
  useLayoutEffect(() => {
    measure();
  });

  useLayoutEffect(() => {
    if (!enabled) return;
    let timer: number | undefined;
    let animationFrame: number | undefined;
    let disposed = false;
    const scheduleMeasurement = (): void => {
      if (disposed) return;
      window.clearTimeout(timer);
      if (animationFrame !== undefined) cancelAnimationFrame(animationFrame);
      timer = window.setTimeout(() => {
        timer = undefined;
        animationFrame = requestAnimationFrame(() => {
          animationFrame = undefined;
          measure();
        });
      }, RESIZE_DELAY);
    };

    const observer = new ResizeObserver(scheduleMeasurement);
    if (tableRef.current) observer.observe(tableRef.current);
    if (footerRef.current) observer.observe(footerRef.current);
    // Siblings above each ancestor include the page heading and the table's filters.
    for (let node: HTMLElement | null = frameRef.current; node; node = node.parentElement) {
      observer.observe(node);
      for (const sibling of Array.from(node.parentElement?.children ?? [])) {
        if (sibling === node) break;
        observer.observe(sibling);
      }
    }
    window.addEventListener('resize', scheduleMeasurement);
    const fonts = document.fonts;
    void fonts?.ready.then(scheduleMeasurement);
    fonts?.addEventListener('loadingdone', scheduleMeasurement);

    return (): void => {
      disposed = true;
      observer.disconnect();
      window.removeEventListener('resize', scheduleMeasurement);
      fonts?.removeEventListener('loadingdone', scheduleMeasurement);
      window.clearTimeout(timer);
      if (animationFrame !== undefined) cancelAnimationFrame(animationFrame);
    };
  }, [enabled, measure, tableRef]);

  return { frameRef, footerRef };
};
